import { Box, Checkbox, Input, Label, Select } from 'theme-ui';
import React from 'react';
import { DeliveryRule } from '../../state/zipcode/types';

// The number of delivery day rules to display, one for each day of the week.
export const NUMBER_OF_DELIVERY_DAY_RULES_FOR_DISPLAY = 7;

// Defaults used when enabling a new delivery rule for a day.
export const DEFAULT_LEAD_TIME = 1;
export const DEFAULT_ORDER_DEADLINE = '11:00';

// Lead time options (in days) offered in the dropdown.
const LEAD_TIME_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

function getDayOfWeek(day: number) {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return days[day];
}

interface DeliveryRulesEditorProps {
  // One entry per weekday (index = delivery day, 0 = Sunday). null = disabled.
  deliveryRules: (DeliveryRule | null)[];
  onToggleDay: (deliveryDay: number, enabled: boolean) => void;
  onLeadTimeChange: (deliveryDay: number, leadTime: string) => void;
  onOrderDeadlineChange: (deliveryDay: number, orderDeadline: string) => void;
}

/**
 * Presentational editor for the per-weekday delivery rules table (Enabled
 * checkbox, lead time, order-deadline cut off). Controlled entirely by the
 * parent via value-based callbacks so it can back either the single-zip modal
 * (auto-save per change) or the bulk modal (build a template, apply once).
 */
const DeliveryRulesEditor = ({
  deliveryRules,
  onToggleDay,
  onLeadTimeChange,
  onOrderDeadlineChange,
}: DeliveryRulesEditorProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Label sx={{ width: '15%' }}>Enabled</Label>
        <Label sx={{ width: '35%' }}>Delivery Day</Label>
        <Label sx={{ width: '25%' }}>Lead time</Label>
        <Label sx={{ width: '25%' }}>Cut off</Label>
      </Box>
      {deliveryRules.map((deliveryRule, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box sx={{ width: '15%' }}>
            <Label>
              <Checkbox
                checked={deliveryRules[index] !== null}
                onChange={(event) => onToggleDay(index, event.target.checked)}
              />
            </Label>
          </Box>
          <Box sx={{ width: '35%' }}>{getDayOfWeek(index)}</Box>
          <Box sx={{ width: '25%' }}>
            {deliveryRules[index] !== null && (
              <Select
                onChange={(event) =>
                  onLeadTimeChange(index, event.target.value)
                }
                value={deliveryRule?.leadTime ?? DEFAULT_LEAD_TIME}
              >
                {LEAD_TIME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </Box>
          <Box sx={{ width: '25%' }}>
            {deliveryRules[index] !== null && (
              <Input
                type="time"
                value={deliveryRule?.orderDeadline ?? DEFAULT_ORDER_DEADLINE}
                onChange={(event) =>
                  onOrderDeadlineChange(index, event.target.value)
                }
              />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DeliveryRulesEditor;
